import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "./AuthContext";
import { useT } from "../i18n/I18nContext";

/**
 * Local-only "fake login" form, shown on the login screen when the backend
 * has DEV_AUTH_BYPASS=true. Lets you click around the UI without setting up
 * a real Google OAuth client. Hard-disabled by the backend in production.
 */
export function DevLoginForm() {
  const { signInWithDev } = useAuth();
  const t = useT();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [email, setEmail] = useState("marcus@agriavetguide.com");
  const [name, setName] = useState("Marcus Christiansson");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .devLoginEnabled()
      .then((r) => {
        if (!cancelled) setEnabled(r.enabled);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (enabled !== true) return null;

  return (
    <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-left">
      <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
        {t("login.dev.title")}
      </div>
      <p className="mt-1 text-xs text-amber-900/80">{t("login.dev.help")}</p>
      <form
        className="mt-3 flex flex-col gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          setLoading(true);
          try {
            await signInWithDev(email.trim(), name.trim() || undefined);
          } catch (e: any) {
            setErr(e?.message ?? t("login.signInFailed"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <label className="text-xs font-medium text-amber-900">
          {t("login.dev.email")}
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-sm text-text-primary"
          />
        </label>
        <label className="text-xs font-medium text-amber-900">
          {t("login.dev.name")}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-sm text-text-primary"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="mt-1 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-60"
        >
          {loading ? t("login.dev.submitting") : t("login.dev.submit")}
        </button>
        {err && <div className="text-xs text-red-700">{err}</div>}
      </form>
    </div>
  );
}
